/**
 * @file The page URL as analytics is allowed to see it.
 *
 * A pageview is sent to PostHog and to the GTM dataLayer on every navigation,
 * and the obvious thing to send — the full href — hands third parties whatever
 * happens to be in the query string. That is not hypothetical: an invitation
 * link is `/join?token=<32 random bytes>`, the bearer credential for a
 * company's roster, and the sign-in bounce re-embeds the whole URL again as
 * `?next=`. A token that reached an analytics property would be readable by
 * every vendor and every colleague with view access, for its full seven-day
 * life — while the schema docblock promises the plaintext "exists exactly once,
 * in the email that carries it".
 *
 * So the query string is dropped by default and a small ALLOWLIST is kept —
 * fail-closed, the same reasoning as `PUBLIC_CONFIG_KEYS` on the server: a
 * denylist would have to be extended every time a route gains a sensitive
 * param, and the cost of forgetting is a leaked secret rather than a lost
 * dimension. A new param is invisible to analytics until somebody decides it
 * is safe, which is the correct default for a decision nobody remembers to
 * make.
 *
 * The hash is dropped outright: nothing reads it, and it is the other place
 * tokens conventionally hide. The PATH is kept — it is the dimension analytics
 * exists to report — except for segments that are opaque ids, which are the
 * third place a credential hides (`OPAQUE_SEGMENT` below).
 *
 * Accepts an absolute URL as well as a path — PostHog derives `$current_url`
 * from `window.location.href`, so the SDK-level sanitizer feeds absolute URLs
 * through here. Splitting on `?` leaves the origin intact either way.
 */

import { MAX_PALLET_QUANTITY } from '@stopal/shared/pallet-spec'

/**
 * A path segment that carries no meaning for analytics and may carry a
 * credential. The query string can be allowlisted because its keys are a small
 * named set; a PATH cannot — the path IS the dimension, so the rule here is
 * necessarily shape-based: a UUID, or a long segment mixing letters and digits.
 *
 * This is not decoration. `/api/doc-media/<uuid>.<ext>` is documented in
 * `apps/api/src/routes/doc-media.ts` as an unguessable id acting as a
 * capability token on a deliberately public GET, and a markdown link to hosted
 * media renders as a plain anchor — so one click reports it as `ui_click.href`
 * and as PostHog's `attr__href`. Better Auth's reset link is the same shape.
 *
 * Ordinary route segments are left alone: they are words, not tokens
 * (`business-account-requests` has no digit; `ORL-AB12CD34` is too short).
 */
const OPAQUE_SEGMENT =
  /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|(?=[\w-]*(?:\d|[a-z][\w-]*[A-Z]|[A-Z][\w-]*[a-z]))[\w-]{20,})$/

/**
 * Split a scheme and authority off the front, so the host is NOT walked as a
 * path segment. It otherwise is one — `https://host/x` splits on `/` into
 * `['https:', '', 'host', 'x']` — and a long mixed-case host label
 * (`mail-attachment-us-east1.google.com`, a preview deploy) was rewritten to
 * `:id`, quietly destroying the referrer dimension this rule exists beside.
 */
function splitAuthority(url: string): [authority: string, path: string] {
  const schemeEnd = url.indexOf('://')
  const authorityStart =
    schemeEnd !== -1 ? schemeEnd + 3 : url.startsWith('//') ? 2 : -1
  if (authorityStart === -1) return ['', url]
  const pathStart = url.indexOf('/', authorityStart)
  return pathStart === -1
    ? [url, '']
    : [url.slice(0, pathStart), url.slice(pathStart)]
}

/**
 * EVERY dot-separated part is tested, not just the stem. Testing the stem alone
 * reads as "name, then extension" and is really "first part, then everything
 * else": a JWT-shaped segment kept its payload and signature (only the constant
 * header matched), and `avatar.<uuid>.png` kept the id. A media extension is
 * still reported — `jpg` matches nothing, so `<uuid>.jpg` becomes `:id.jpg`.
 */
function redactSegment(segment: string): string {
  if (!segment) return segment
  return segment
    .split('.')
    .map((part) => (OPAQUE_SEGMENT.test(part) ? ':id' : part))
    .join('.')
}

function redactOpaqueSegments(path: string): string {
  return path.split('/').map(redactSegment).join('/')
}

const ENUM_VALUE = /^[a-z][a-z_-]{0,15}$/

/**
 * The value shape for a param whose values are a known small set.
 *
 * A KEY on the allowlist is not a promise about its VALUE: any route may reuse
 * a key for something else, and the allowlist decision was made for only one of
 * the meanings. `/auth/native` did exactly that — it carried a per-sign-in
 * handoff id under `flow`, a three-value funnel dimension (`QUOTE_FLOWS` is
 * store / rent / move).
 *
 * **This guard is what stopped that reaching anywhere**, and it is the half
 * that generalises: a 43-character hash fails it on length, case and digits, so
 * no destination ever saw the id through `analyticsUrl`. What a value guard
 * cannot repair is the premise — that a KEY names a small, stable, safe value
 * set — so the route was also renamed onto `NATIVE_AUTH_HANDOFF_PARAM`, which
 * `native-auth-param.test.ts` pins outside `PARAM_VALUE_GUARDS`. The rename
 * fixes the one route; this guard covers the next one nobody has written.
 */
const isEnumValue = (value: string): boolean => ENUM_VALUE.test(value)

/**
 * A pallet count, canonical and in the range the hand-off itself accepts.
 *
 * Four independent checks:
 *
 * - **Digits only, tested BEFORE `Number`.** `Number` coerces `' 5'`, `+5`,
 *   `5e1` and `0x10` into range; the regex is what stops them.
 * - **No hand-typed digit ceiling.** A `{1,4}` would be an independent
 *   bound of 9999 sitting under a docblock claiming the bound is read from
 *   `MAX_PALLET_QUANTITY` — so raising the shared constant past 9999 would
 *   silently drop legitimate counts, which is the exact coupling failure the
 *   claim would deny.
 * - **The canonical form is what bounds LENGTH, and the range test is not.**
 *   A longer digit string is not a larger number: a padded string is longer
 *   without being larger, so `'0'.repeat(400) + '5'` passes the regex,
 *   converts to `5`, and **passes the range test**. `String(count) === value`
 *   is the only line rejecting it. Written out because treating length as a
 *   proxy for magnitude invites exactly one edit — normalise padding instead
 *   of refusing it — that would delete the line doing the work and admit an
 *   arbitrarily long correlator. The padded case is pinned by a test rather
 *   than left to this paragraph.
 * - **The canonical form is required, not merely the value.** Testing the
 *   NUMBER while keeping the STRING would let `2`, `02` and `0002` through as
 *   three distinct values of one dimension — and via PostHog's `$set_once` a
 *   padded form lands permanently on a person profile. No route mints one, so
 *   demanding the canonical spelling costs nothing and closes it.
 */
function isPalletCountValue(value: string): boolean {
  if (!/^[0-9]+$/.test(value)) return false
  const count = Number(value)
  if (count < 1 || count > MAX_PALLET_QUANTITY) return false
  return String(count) === value
}

/**
 * No shape constraint on this param's value — a decision on the record rather
 * than an absence.
 *
 * **Use this constant, never an equivalent inline `() => true`.** `DEDUPED_PARAMS`
 * below identifies the pass-through params by reference equality against it, so
 * an inline twin is silently treated as guarded and its key gets deduplicated.
 * The failure direction is safe (narrower egress, never a leak) and it would
 * still contradict what this file says it does.
 */
const KEEP_ANY_VALUE = (): boolean => true

/**
 * Every param analytics may see, each with the test its VALUE must pass.
 *
 * **This map IS the allowlist** — membership and value test are one
 * declaration, so adding a key without deciding about its values is not
 * expressible. Separate `Set`s of keys and of guarded keys would make
 * "allowlisted but unguarded" the thing you got by not noticing the second
 * structure twelve lines down.
 */
const PARAM_VALUE_GUARDS = new Map<string, (value: string) => boolean>([
  // Which service the visitor picked on the landing hero — the one param that
  // genuinely explains a checkout funnel.
  ['flow', isEnumValue],
  // Consumer vs business, so the two products' funnels can be told apart.
  ['brand', isEnumValue],
  // The estimator hand-off count, and the only dimension that distinguishes an
  // estimator-originated checkout from any other one — without it there is no
  // way to answer whether a public acquisition tool sold anything. A count says
  // nothing about the customer beyond how much they have. Its sibling `kit` is
  // deliberately absent: a product id is a statement about their belongings.
  ['pallets', isPalletCountValue],
  // Campaign attribution. Not a concession to the allowlist — these ARE the
  // dimension: GA4 reads them off `page_location`, so dropping them files every
  // paid visit under (direct)/(none).
  //
  // Unguarded because GA4 needs the value verbatim as the attribution string,
  // and it is never OUR secret — NOT because nobody controls it. Anyone who
  // writes a link picks these, so `?utm_content=<per-visitor id>` is the same
  // correlator the guarded params refuse. That is a known and accepted gap, not
  // a case this map covers.
  ['utm_source', KEEP_ANY_VALUE],
  ['utm_medium', KEEP_ANY_VALUE],
  ['utm_campaign', KEEP_ANY_VALUE],
  ['utm_term', KEEP_ANY_VALUE],
  ['utm_content', KEEP_ANY_VALUE],
  ['gclid', KEEP_ANY_VALUE],
  ['fbclid', KEEP_ANY_VALUE],
  ['msclkid', KEEP_ANY_VALUE],
])

/**
 * A guarded key is kept ONCE.
 *
 * A per-value guard bounds one value and says nothing about how many times the
 * key appears, so `?pallets=42&pallets=17&pallets=99&…` reassembles exactly the
 * long digit correlator `isPalletCountValue` exists to refuse — every value
 * passes, and each is appended. Guarding the value while ignoring multiplicity
 * is a bound the docblock claims and the code does not have.
 *
 * Only GUARDED keys are deduplicated: a repeat of one is anomalous (no route
 * mints two), while the campaign params keep the pass-through behaviour GA4
 * expects. This narrows what leaves and cannot drop a value any route emits.
 */
const DEDUPED_PARAMS = new Set(
  [...PARAM_VALUE_GUARDS]
    .filter(([, guard]) => guard !== KEEP_ANY_VALUE)
    .map(([key]) => key),
)

/**
 * The allowlisted keys, derived from the map rather than restated, so a key
 * cannot be listed here without also carrying a value guard.
 *
 * Exported for one caller: `native-auth-param.test.ts` asserts the native
 * sign-in handoff param is NOT a member. That is the collision this file's
 * `isEnumValue` docblock describes, expressed where it can fail — reachable
 * from neither declaration alone.
 *
 * **Never branch on this to decide whether a param is kept — call
 * `isKeptParam`.** Membership is half a decision; the value still has to pass
 * its guard, and a standalone key Set would skip that half. It is re-exposed
 * here for an assertion, not for a second code path.
 */
export const ANALYTICS_PARAM_KEYS: ReadonlySet<string> = new Set(
  PARAM_VALUE_GUARDS.keys(),
)

function isKeptParam(key: string, value: string): boolean {
  // Fail-closed on BOTH axes: an unlisted key has no guard and is dropped, and
  // a listed one is dropped unless its value passes. There is deliberately no
  // `!guard ||` branch — that spelling keeps every unlisted key, which is the
  // whole file inverted.
  return PARAM_VALUE_GUARDS.get(key)?.(value) ?? false
}

/**
 * Sanitise a URL for analytics: the path kept with opaque segments redacted,
 * the query reduced to `PARAM_VALUE_GUARDS`, the hash dropped outright.
 *
 * The file header above is the argument for why this exists at all; this is the
 * function every consumer reaches it through — `router.tsx`'s pageview
 * subscription, `gtm.ts`'s `pushEvent`, and PostHog's own sanitizer hook.
 */
export function analyticsUrl(href: string): string {
  const [pathAndQuery = ''] = href.split('#')
  const [rawPathname = '', query] = pathAndQuery.split('?')
  const [authority, path] = splitAuthority(rawPathname)
  const pathname = authority + redactOpaqueSegments(path)
  if (!query) return pathname

  const kept = new URLSearchParams()
  for (const [key, value] of new URLSearchParams(query)) {
    if (!isKeptParam(key, value)) continue
    if (DEDUPED_PARAMS.has(key) && kept.has(key)) continue
    kept.append(key, value)
  }
  const suffix = kept.toString()
  return suffix ? `${pathname}?${suffix}` : pathname
}

/**
 * A root-relative path (`/join`), a protocol-relative reference (`//host/x`),
 * or an absolute URL with an authority (`https://host/x`) — the shapes a URL
 * takes by the time it is an analytics property. `anchor.href` is absolutised
 * by the DOM and the router hands out paths, so a URL reaching analytics from
 * this app is one of them.
 *
 * A scheme must be followed by `//`, so an ordinary sentence is not mistaken
 * for a URL: a label like `Note:/home is best?` would otherwise be cut at its
 * question mark, silently corrupting the `text` property pushed on every click.
 * A value NOT matched here is left entirely alone — which is why a key known to
 * be a URL is also sanitised by name at each call site, whatever shape its
 * value arrives in.
 */
const URL_SHAPED = /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:\/\/|\/)/

/** `analyticsUrl`, but only for URL-shaped values. Others pass through. */
export function analyticsUrlIfUrlShaped(value: string): string {
  return URL_SHAPED.test(value) ? analyticsUrl(value) : value
}

/**
 * Only a `{}` literal is walked — rebuilding an arbitrary object from its own
 * entries would hand analytics `{}` in place of a `Date` or a `URL`. The check
 * is the string tag plus a one-link prototype chain rather than an identity
 * test against this realm's `Object.prototype`, so a plain object from another
 * realm (an iframe, the native WebView) is still walked.
 *
 * A class instance is therefore NOT walked. That is an accepted edge only
 * because such a value is INERT — see `sanitizeValue`, which special-cases the
 * one class that is not.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== '[object Object]') return false
  const proto = Object.getPrototypeOf(value)
  return proto === null || Object.getPrototypeOf(proto) === null
}

/**
 * `seen` maps each container to the copy standing in for it, so a cycle
 * resolves to the SANITISED copy. A depth cap did this job before and did it
 * fail-open: it returned the original node by reference, which spliced both the
 * live cycle and every unsanitised string under it straight into the payload
 * handed to `dl.push` — in the one file whose whole argument is that forgetting
 * must cost a dimension rather than a secret.
 */
function sanitizeValue(value: unknown, seen: Map<object, unknown>): unknown {
  if (typeof value === 'string') return analyticsUrlIfUrlShaped(value)
  if (value === null || typeof value !== 'object') return value
  // A `URL` is the one non-plain object that is NOT inert: it has a `toJSON`,
  // so it does not arrive at the destination as an opaque object — it arrives
  // as its full href, query string and all. "Skipping it loses nothing" is the
  // reasoning that applies to a `Date`; here it would ship the credential.
  // Checked by string tag so an instance from another realm is caught too.
  if (Object.prototype.toString.call(value) === '[object URL]') {
    return analyticsUrl(String(value))
  }
  const already = seen.get(value)
  if (already !== undefined) return already
  if (Array.isArray(value)) {
    const out: unknown[] = []
    seen.set(value, out)
    for (const entry of value) out.push(sanitizeValue(entry, seen))
    return out
  }
  if (isPlainObject(value)) return sanitizeBag(value, seen)
  return value
}

function sanitizeBag(
  bag: Record<string, unknown>,
  seen: Map<object, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  seen.set(bag, out)
  for (const [key, entry] of Object.entries(bag)) {
    out[key] = sanitizeValue(entry, seen)
  }
  return out
}

/**
 * Run an analytics payload through the allowlist, keyed on the VALUE being a
 * URL rather than on the key being one of a handful of names.
 *
 * The key-list version of this covered `page_path`, `page_location` and
 * `page_referrer`, which is every URL property that existed the day it was
 * written. `ui_click` then added `href` — the clicked anchor's whole URL — and
 * inherited nothing, because a key list cannot cover a key nobody has invented
 * yet. Any URL-carrying property is sanitised here whatever it is called and
 * at any nesting depth, cycles included.
 */
export function sanitizeAnalyticsProps(
  props: Record<string, unknown>,
): Record<string, unknown> {
  return sanitizeBag(props, new Map())
}
